import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

export type OldestNotSentBackStatusColor = "green" | "amber" | "red";

interface UseOldestNotSentBackAgeOptions {
  customerIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface OldestNotSentBackAgeResult {
  statusColor: OldestNotSentBackStatusColor;
  days: number;
  oldestDate: string | null;
}

export function useOldestNotSentBackAge(options?: UseOldestNotSentBackAgeOptions) {
  const customerIds = options?.customerIds;
  const dateFrom = options?.dateFrom;
  const dateTo = options?.dateTo;

  const query = useQuery<OldestNotSentBackAgeResult>({
    queryKey: ["oldest-not-sent-back-age", customerIds?.join(","), dateFrom, dateTo],
    queryFn: async () => {
      let q = supabase
        .from("encounter_results_approved_unwritten")
        .select("observation_datetime")
        .order("observation_datetime", { ascending: true })
        .limit(1);

      if (customerIds && customerIds.length > 0) {
        q = q.in("customer_id", customerIds);
      }
      if (dateFrom) {
        q = q.gte("observation_datetime", dateFrom);
      }
      if (dateTo) {
        q = q.lte("observation_datetime", dateTo);
      }

      const { data, error } = await q;
      if (error) throw error;

      const oldest = data?.[0];
      if (!oldest?.observation_datetime) {
        return { statusColor: "green", days: 0, oldestDate: null };
      }

      // Calculate days since observation
      const nowMs = Date.now();
      const oldestMs = new Date(oldest.observation_datetime).getTime();
      const days = Math.max(0, Math.floor((nowMs - oldestMs) / 86_400_000));

      const statusColor: OldestNotSentBackAgeResult["statusColor"] = days <= 3 ? "green" : days <= 7 ? "amber" : "red";

      return {
        statusColor,
        days,
        oldestDate: oldest.observation_datetime,
      };
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    statusColor: query.data?.statusColor ?? "green",
    days: query.data?.days ?? 0,
    oldestDate: query.data?.oldestDate ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
